<script>
import { coresNomes } from '@constants/Color'
import { REPORT_PALETTE } from '@modules/consolidator/constants/Color'
import {
  WALLET_EMPTY_MESSAGE,
  ALOCACAO_VAZIOS,
  ALOCACAO_VISAO,
  ALOCACAO_VAZIOS_OPTIONS,
  ALOCACAO_VISAO_OPTIONS,
} from '@modules/consolidator/constants/Text'
import { toMoney } from '@utils/Format'
import { getCurrencyByInvestmentType } from '@modules/consolidator/utils/InvestmentType/InvestmentType.js'
import {
  allocationTotal,
  allocationPercent,
} from '@modules/consolidator/utils/allocation-percent/allocation-percent'
import FeedbackWrapper from '@modules/global/components/feedback-wrapper/feedback-wrapper.vue'
import AlocacaoEstrategicaChartBody from '@modules/consolidator/components/alocacao-estrategica/alocacao-estrategica-chart-body.vue'

export default {
  name: 'AlocacaoEstrategica',
  components: {
    FeedbackWrapper,
    AlocacaoEstrategicaChartBody,
  },
  props: {
    valuesChart: {
      type: Array,
      default: () => [],
    },
    optionsChat: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    investmentType: {
      type: String,
      default: '',
    },
    contentOnly: {
      type: Boolean,
      default: false,
    },
    colorsByLabel: {
      type: Object,
      default: null,
    },
    suggestedValues: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // Default Sugerido (AZ-5704b916): abre em atual+sugerida quando há composição-alvo;
      // sem dados sugeridos o toggle nem aparece e a visão segue Atual.
      showSuggested: true,
      // hideEmpty:true e o default de AZ-a96ea5cc (#2032, upstream) — sem relação com
      // esta task, preservado do merge.
      hideEmpty: true,
      WALLET_EMPTY_MESSAGE,
      ALOCACAO_VAZIOS_OPTIONS,
      ALOCACAO_VISAO_OPTIONS,
    }
  },
  computed: {
    emptyMode: {
      get() {
        return this.hideEmpty
          ? ALOCACAO_VAZIOS.COM_VALOR
          : ALOCACAO_VAZIOS.TODAS
      },
      set(mode) {
        this.hideEmpty = mode === ALOCACAO_VAZIOS.COM_VALOR
      },
    },
    viewMode: {
      get() {
        return this.showSuggested
          ? ALOCACAO_VISAO.SUGERIDO
          : ALOCACAO_VISAO.ALOCACAO
      },
      set(mode) {
        this.showSuggested = mode === ALOCACAO_VISAO.SUGERIDO
      },
    },
    hasSuggestedData() {
      return this.suggestedValues && this.suggestedValues.length > 0
    },
    isSuggestedActive() {
      return this.showSuggested && this.hasSuggestedData
    },
    hasData() {
      const labels = (this.chartData && this.chartData.data.labels) || []
      return this.hasNonZeroValues && labels.length > 0
    },
    hasNonZeroValues() {
      const values = this.valuesChart
      return values && values.some((value) => value !== 0)
    },
    processedData() {
      const items = this.prepareItems() || []
      const total = items.reduce((acc, item) => acc + item.value, 0)
      return { items, total }
    },
    rawTotal() {
      return this.processedData.total
    },
    itemsWithColor() {
      return this.processedData.items
    },
    chartData() {
      if (!this.hasNonZeroValues) return null

      const items = this.itemsWithColor
      if (!items || items.length === 0) return null

      const chartResult = this.buildChartResult(items)

      return this.isSuggestedActive
        ? this.buildSuggestedChartData(chartResult)
        : this.buildDefaultChartData(chartResult)
    },
    percentages() {
      if (!this.chartData || !this.chartData.data) return []
      const dataset =
        this.chartData.data.datasets && this.chartData.data.datasets['Alocação']
      return (dataset && dataset.porcentagens) || []
    },
    shouldScrollChart() {
      return this.chartHeight > 500
    },
    chartHeight() {
      const data = this.chartData && this.chartData.data
      if (!data || !data.labels) return 0
      return Math.max(400, data.labels.length * 55)
    },
    total() {
      return toMoney(this.rawTotal, 2, this.currency)
    },
    currency() {
      return getCurrencyByInvestmentType({
        investmentType: this.investmentType,
      })
    },
  },
  watch: {
    // `showSuggested` dispara só no toggle (o mount com dados é emitido pelo watcher
    // de `hasSuggestedData` abaixo — immediate nos dois emitiria duplicado).
    showSuggested(value) {
      if (!this.hasSuggestedData) return
      this.$emit('update:viewMode', value ? 'sugerido' : 'alocacao')
    },
    // O prop `suggestedValues` costuma chegar DEPOIS do mount (carteira carrega async):
    // quando os dados sugeridos chegarem, sincroniza o pai com o modo corrente.
    hasSuggestedData: {
      immediate: true,
      handler(hasData) {
        if (!hasData) return
        this.$emit('update:viewMode', this.showSuggested ? 'sugerido' : 'alocacao')
      },
    },
  },
  methods: {
    buildChartResult(items) {
      const total = allocationTotal(items.map((item) => item.value))

      return items.reduce(
        (acc, item) => {
          const value = item.value || 0
          acc.labels.push(item.label)
          acc.values.push(value)
          acc.colors.push(value < 0 ? coresNomes.vermelhoControle : item.color)
          acc.percentages.push(allocationPercent(value, total))
          return acc
        },
        { labels: [], values: [], colors: [], percentages: [] }
      )
    },
    buildDefaultChartData(chartResult) {
      const alocadosPct = chartResult.percentages.map((pct) => pct * 100)
      const result = this.applyEmptyFilter(
        chartResult.labels,
        alocadosPct,
        chartResult.colors,
        chartResult.percentages,
        chartResult.values
      )
      return {
        mode: 'porcentagem',
        currency: this.currency,
        legend: false,
        data: {
          labels: result.labels,
          datasets: {
            Alocação: {
              dados: result.values,
              colors: result.colors,
              porcentagens: result.percentages,
              rawValues: result.rawValues,
              mode: 'monetario_abreviado',
            },
          },
        },
      }
    },
    buildSuggestedChartData(chartResult) {
      const suggestedByLabel = this.buildSuggestedByLabel()
      const alocadosPct = chartResult.percentages.map((pct) => pct * 100)
      const sugeridosPct = chartResult.labels.map(
        (label) => suggestedByLabel[label] || 0
      )
      const result = this.applyEmptyFilterWithSuggested(
        chartResult.labels,
        alocadosPct,
        sugeridosPct,
        chartResult.colors,
        chartResult.values
      )
      return {
        mode: 'porcentagem',
        currency: this.currency,
        legend: false,
        data: {
          labels: result.labels,
          datasets: {
            Alocação: {
              dados: result.alocados,
              colors: result.colors,
              porcentagens: result.percentages,
              rawValues: result.rawValues,
              mode: 'monetario_abreviado',
            },
            Sugerido: {
              dados: result.sugeridos,
              color: coresNomes.cinza,
            },
          },
        },
      }
    },
    applyEmptyFilter(labels, values, colors, percentages, rawValues) {
      if (!this.hideEmpty) {
        return { labels, values, colors, percentages, rawValues }
      }
      const result = {
        labels: [],
        values: [],
        colors: [],
        percentages: [],
        rawValues: [],
      }
      labels.forEach((label, index) => {
        if (rawValues[index] !== 0) {
          result.labels.push(label)
          result.values.push(values[index])
          result.colors.push(colors[index])
          result.percentages.push(percentages[index])
          result.rawValues.push(rawValues[index])
        }
      })
      return result
    },
    applyEmptyFilterWithSuggested(
      labels,
      alocados,
      sugeridos,
      colors,
      rawValues
    ) {
      if (!this.hideEmpty) {
        return {
          labels,
          alocados,
          sugeridos,
          colors,
          rawValues,
          percentages: alocados.map((value) => value / 100),
        }
      }
      const result = {
        labels: [],
        alocados: [],
        sugeridos: [],
        colors: [],
        rawValues: [],
        percentages: [],
      }
      labels.forEach((label, index) => {
        const hasValue = rawValues[index] !== 0 || sugeridos[index] !== 0
        if (hasValue) {
          result.labels.push(label)
          result.alocados.push(alocados[index])
          result.sugeridos.push(sugeridos[index])
          result.colors.push(colors[index])
          result.rawValues.push(rawValues[index])
          result.percentages.push(alocados[index] / 100)
        }
      })
      return result
    },
    buildSuggestedByLabel() {
      const labelMap = {}
      const optionsLabels = (this.optionsChat && this.optionsChat.labels) || []
      optionsLabels.forEach((label, index) => {
        const trimmed = (label || '').toString().trim()
        labelMap[trimmed] = this.suggestedValues[index] || 0
      })
      return labelMap
    },
    resolveItemColor(label, fallbackIndex) {
      if (this.colorsByLabel && this.colorsByLabel[label]) {
        return this.colorsByLabel[label]
      }
      return REPORT_PALETTE[fallbackIndex % REPORT_PALETTE.length]
    },
    prepareItems() {
      const labels = (this.optionsChat && this.optionsChat.labels) || []
      const values = this.valuesChart || []
      if (!labels.length || !values.length) return []

      return labels
        .map((label, i) => ({
          label: (label || '').toString().trim(),
          value: values[i] || 0,
          originalIndex: i,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((item, index) => ({
          ...item,
          color: this.resolveItemColor(item.label, index),
        }))
        .sort((a, b) => b.value - a.value)
    },
  },
}
</script>

<template>
  <div
    v-if="!contentOnly"
    class="alocacao-estrategica az-card-bordered"
    :class="{ 'alocacao-estrategica--loading': loading }"
  >
    <div v-if="!loading" class="alocacao-estrategica__header">
      <span class="alocacao-estrategica__title az-ds-card-title">
        Alocação Estratégica
      </span>
      <v-spacer />
      <div class="d-flex align-center az-gap-large">
        <BaseToggleButton
          v-if="hasSuggestedData"
          v-model="viewMode"
          role="group"
          aria-label="Visão da alocação"
          small
          buttonStyle="flatGray"
          :options="ALOCACAO_VISAO_OPTIONS"
        />
        <BaseToggleButton
          v-model="emptyMode"
          role="group"
          aria-label="Classes exibidas"
          small
          buttonStyle="flatGray"
          :options="ALOCACAO_VAZIOS_OPTIONS"
        />
        <div
          v-if="$slots['agrupar-por']"
          class="alocacao-estrategica__agrupar"
        >
          <slot name="agrupar-por"></slot>
        </div>
      </div>
    </div>
    <FeedbackWrapper
      :loading="loading"
      :noData="!hasData"
      :message="WALLET_EMPTY_MESSAGE"
      :minHeight="425"
    >
      <div class="alocacao-estrategica__content">
        <div
          class="alocacao-estrategica__chart-scroll"
          :class="{ 'alocacao-estrategica__chart-scroll--active': shouldScrollChart }"
        >
          <AlocacaoEstrategicaChartBody
            :hasData="hasData"
            :chartData="chartData"
            :height="chartHeight"
          />
        </div>

        <div class="alocacao-estrategica__content-right">
          <div class="alocacao-estrategica__total">
            <div class="az-text-primary az-size-14 font-medium">Total</div>
            <div class="az-text-primary az-size-14 font-bold">
              {{ total }}
            </div>
          </div>
        </div>
      </div>

      <div class="info-adicional">
        <slot name="info-adicional"></slot>
      </div>
    </FeedbackWrapper>
  </div>
  <AlocacaoEstrategicaChartBody
    v-else
    :hasData="hasData"
    :chartData="chartData"
    noPadding
  />
</template>

<style lang="sass">
.alocacao-estrategica
  padding-right: 38px !important

  &--loading
    padding: 0 !important

  &__header
    display: flex
    align-items: center
    margin-bottom: 16px

  &__content
    display: flex
    justify-content: space-between
    width: 100%
    box-sizing: border-box
    gap: 16px

    &-chart,
    &-empty
      flex: 1
      min-width: 300px
      box-sizing: border-box

    &-chart--no-padding
      padding-block: 0

    &-empty
      display: flex
      align-items: center
      justify-content: center

  &__total
    display: flex
    flex-direction: column
    gap: 8px
    padding-bottom: 24px
    white-space: nowrap

  &__chart-scroll
    flex: 1
    min-width: 0
    overflow: hidden

    &--active
      overflow-y: auto
      max-height: 500px

  &__agrupar
    width: 100%
    min-width: 100px
    max-width: 150px

  .component-bars-horizontal,
  .component-bars
    border-radius: 0px
    padding-block: 0px
    box-shadow: none

    div
      margin-top: 0px !important
      max-height: 500px !important
</style>
